import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, Clock, User, ArrowLeft, Share2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const MindfulnessDaily = () => {
  const navigate = useNavigate();

  const handleGoBack = () => {
    navigate('/blog');
  };

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-4xl mx-auto">
          <Button 
            variant="ghost" 
            onClick={handleGoBack}
            className="mb-8 -ml-4 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Blog
          </Button>
          
          <div className="mb-8">
            <Badge className="mb-4">Mindfulness</Badge>
            <h1 className="text-4xl md:text-6xl font-serif font-light mb-6 text-foreground leading-tight">
              Mindfulness – A Daily Practice of Presence
            </h1>
            
            <div className="flex items-center gap-6 text-sm text-muted-foreground mb-8">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>July 12, 2025</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>5 min read</span>
              </div>
              <div className="flex items-center gap-2">
                <User className="w-4 h-4" />
                <span>Piotr</span>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <Button variant="outline" size="sm">
                <Share2 className="w-4 h-4 mr-2" />
                Share Article
              </Button>
            </div>
          </div>
          
          <div className="relative overflow-hidden rounded-2xl mb-12">
            <img 
              src="/lovable-uploads/eb2e3123-3b3d-403c-accb-13d1268e4b02.png" 
              alt="Person meditating on mountain peak overlooking vast mountain ranges representing mindful presence"
              className="w-full h-[400px] md:h-[500px] object-cover grayscale"
            />
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="px-6 pb-24">
        <div className="max-w-4xl mx-auto">
          <div className="prose prose-lg max-w-none">
            <div className="text-xl leading-relaxed text-muted-foreground mb-8 font-light italic">
              In a fast-paced world, mindfulness has emerged as a simple yet profound way to reclaim presence. At its core, mindfulness is about paying attention to the present moment, without judgment. It is both an ancient practice and a modern tool, validated by decades of scientific research.
            </div>
            
            <div className="space-y-8 text-lg leading-relaxed text-foreground">
              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">The science of mindfulness</h2>

              <p className="mb-6 leading-relaxed">
                Research demonstrates that mindfulness meditation reduces stress, improves concentration, and enhances emotional regulation (Kabat-Zinn, 1990; Tang et al., 2015). Neuroimaging studies reveal that <strong className="text-accent">mindfulness practice strengthens areas of the brain associated with attention and compassion</strong> (Hölzel et al., 2011).
              </p>

              <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent my-12"></div>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">Everyday mindfulness</h2>

              <p className="mb-6 leading-relaxed">
                Mindfulness doesn't require hours of sitting meditation. It can be integrated into daily life:
              </p>

              <ul className="space-y-3 ml-6 mb-6">
                <li><strong className="text-accent">Eating slowly</strong>, noticing taste and texture.</li>
                <li><strong className="text-accent">Walking while observing</strong> surroundings with curiosity.</li>
                <li><strong className="text-accent">Pausing to take three deep breaths</strong> before responding to an email.</li>
                <li><strong className="text-accent">Listening fully</strong> in conversations.</li>
              </ul>

              <p className="mb-6 leading-relaxed">
                These micro-practices train the mind to stay present, reducing autopilot behaviors.
              </p>

              <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent my-12"></div>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">Practical benefits</h2>

              <p className="mb-6 leading-relaxed">
                Mindfulness is linked to <strong className="text-accent">improved immune function, reduced symptoms of anxiety and depression, and better sleep</strong> (Creswell, 2017). In workplaces, mindfulness training has been shown to enhance resilience and reduce burnout.
              </p>

              <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent my-12"></div>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">Conclusion</h2>

              <p className="mb-6 leading-relaxed">
                Mindfulness is not about escaping reality but engaging with it more deeply. By practicing presence, we cultivate clarity, compassion, and balance in everyday life.
              </p>

              <p className="text-xl font-light italic text-accent mb-6">
                In the stillness of presence, we find our truest strength.
              </p>

              <div className="bg-card p-8 rounded-2xl mt-16 border border-border/20">
                <h3 className="text-2xl font-serif font-light mb-4 text-foreground">References</h3>
                <div className="space-y-2 text-sm text-muted-foreground">
                  <p>• Kabat-Zinn, J. (1990). <em>Full Catastrophe Living</em>. Delta.</p>
                  <p>• Tang, Y. Y., Hölzel, B. K., & Posner, M. I. (2015). The neuroscience of mindfulness meditation. <em>Nature Reviews Neuroscience, 16</em>(4), 213–225.</p>
                  <p>• Hölzel, B. K., et al. (2011). Mindfulness practice leads to increases in regional brain gray matter density. <em>Psychiatry Research: Neuroimaging, 191</em>(1), 36–43.</p>
                  <p>• Creswell, J. D. (2017). Mindfulness interventions. <em>Annual Review of Psychology, 68</em>, 491–516.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default MindfulnessDaily;